<template>
  <GlobalNav>
    <a name="top" />
    <div class="container my-4">
      <div class="row justify-content-center">
        <div class="content">
          <div v-if="showCustomContent" class="tw-border-t tw-border-border mt-5 section">
            <div class="d-flex justify-content-between mb-4">
              <div class="d-flex align-items-center justify-content-around partner-logo">
                <img :src="partnerLogo" :alt="partnerName" :width="250" />
              </div>
              <div class="d-flex flex-column justify-content-center partner-message">
                <div class="mb-2">
                  Thought about hosting yet? {{ partnerName }} recommends Nexcess, a trusted hosting
                  provider for businesses worldwide. Nexcess takes the stress out of launching your
                  site, so you can get up and running in no time.
                  <Tooltip
                    content="Nexcess managed hosting delivers superior speed, security and scalability for your
                  professional website. Plus the industry’s best support, available 24/7/365."
                  >
                    <font-awesome-icon
                      icon="fa-regular fa-circle-question"
                      class="tw-text-[20px]"
                    />
                  </Tooltip>
                </div>
              </div>
            </div>
            <div class="tw-grid tw-grid-cols-3 tw-text-center">
              <a href="#about-nexcess">About Nexcess</a>
              <a href="#what-is-managed-hosting">What is Managed Hosting?</a>
              <a href="#compare-hosting-providers">Comparing Hosting Providers</a>
            </div>
          </div>
          <div class="d-flex justify-content-between p-4 tw-border-b tw-border-border">
            <form class="tw-flex tw-items-center" @submit.prevent="searchDomains">
              <FormInput
                v-model="searchValue"
                data-test="domain-search-input"
                placeholder="example"
                class="me-2 !tw-mb-0"
              />
              <Btn
                data-test="domain-search-button"
                type="submit"
                :loading="searchStatus.isLoading"
                :disabled="searchStatus.isLoading || !searchValue.length"
              >
                Search
              </Btn>
            </form>
            <div>
              <DropDownButton
                size="small"
                variant="secondary"
                :disabled="searchStatus.isLoading || !searchValue.length"
                data-test="show-cart-button"
              >
                <template #button>
                  <font-awesome-icon
                    icon="fa-regular fa-cart-shopping"
                    class="tw-text-[18px] tw-mr-2"
                  />
                  <div class="text-nowrap" data-test="cart-item-count">
                    {{ `View Cart (${cartItems.length})` }}
                  </div>
                </template>
                <template #body>
                  <div class="my-2 mx-2 cart-preview">
                    <div class="h4">Cart</div>
                    <div
                      v-for="(item, i) in cartItems"
                      :key="i"
                      class="tw-border-b tw-border-border py-4 d-flex justify-content-between tw-items-center"
                    >
                      <div :data-test="`cart-item-${i}`">
                        {{ item.domainName }}.{{ item.tld.identity }}
                      </div>
                      <div class="tw-flex tw-items-center tw-pl-2">
                        <div class="fw-bold">
                          {{ moneyFormat(item.package.pricing["12"]) }}/year
                        </div>
                        <IconButton
                          @click="removeItem(item)"
                          :icon="{
                            default: { icon: 'fa-regular fa-cart-minus' },
                            hover: { icon: 'fa-solid fa-cart-minus' },
                          }"
                          class="tw-text-[20px] cart-button"
                          data-test="cart-summary-item-remove-button"
                        />
                      </div>
                    </div>
                    <div class="d-flex justify-content-between mt-3">
                      <div class="h4 w-50">Total</div>
                      <div class="w-75 d-flex justify-content-end">
                        <div>
                          <div class="fw-bold h2">{{ moneyFormat(total) }} Yearly</div>
                          <small class="tw-text-text-weak">Taxes and averages may apply</small>
                        </div>
                      </div>
                    </div>
                  </div>
                </template>
              </DropDownButton>
            </div>
          </div>
          <template v-if="!status.isUninitialized">
            <div class="container px-0">
              <LoadingState class="my-5" v-if="status.isLoading" :size="50" />
              <template v-else>
                <div
                  v-if="isAvailable"
                  class="d-flex justify-content-between py-4 px-4 tw-border-b tw-border-border"
                >
                  <div class="d-flex align-items-center">
                    <font-awesome-icon
                      icon="fa-regular fa-party-horn"
                      class="tw-text-success-icon happy-icon"
                    />
                    <div class="domain-message d-flex">
                      <div data-test="domain-available" class="pe-1">Your domain is available!</div>
                      <div class="d-flex fw-bold" data-test="searched-domain">
                        {{ domain }}.{{ tld }}
                      </div>
                    </div>
                  </div>
                  <div class="d-flex align-items-center">
                    <div>{{ moneyFormat(selectedPackage.pricing["12"]) }} / year</div>
                    <IconButton
                      v-if="selectedPackage.inCart"
                      @click="removePackage(selectedPackage)"
                      :icon="{
                        default: { icon: 'fa-regular fa-cart-minus' },
                        hover: { icon: 'fa-solid fa-cart-minus' },
                      }"
                      class="tw-flex tw-text-[20px] cart-button"
                      data-test="remove-searched-domain"
                    />
                    <IconButton
                      v-else
                      @click="selectPackage(selectedPackage)"
                      :icon="{
                        default: { icon: 'fa-regular fa-cart-plus' },
                        hover: { icon: 'fa-solid fa-cart-plus' },
                      }"
                      class="tw-flex tw-text-[20px] cart-button"
                      data-test="add-searched-domain"
                    />
                  </div>
                </div>
                <div v-else class="py-4 px-4 tw-border-b tw-border-border">
                  <div class="d-flex align-items-center">
                    <font-awesome-icon
                      icon="fa-regular fa-face-frown"
                      class="tw-text-neutral-icon sad-icon"
                    />
                    <div class="domain-message d-flex">
                      <div data-test="domain-unavailable" class="pe-1">We are sorry,</div>
                      <div class="d-flex fw-bold" data-test="searched-domain">
                        {{ domain }}.{{ tld }}
                      </div>
                      <div class="ps-1">is not available.</div>
                    </div>
                  </div>
                </div>
                <div
                  v-for="(pkg, i) in alternativePackages"
                  :key="i"
                  class="cart-item-row d-flex justify-content-between py-3 px-4 tw-border-b tw-border-border"
                >
                  <div :data-test="`alternative-domain-${i}`">{{ pkg.domain }}</div>
                  <div class="d-flex">
                    <div>{{ moneyFormat(pkg.pricing["12"]) }} / year</div>
                    <IconButton
                      v-if="pkg.inCart"
                      @click="removePackage(pkg)"
                      :icon="{
                        default: { icon: 'fa-regular fa-cart-minus' },
                        hover: { icon: 'fa-solid fa-cart-minus' },
                      }"
                      class="tw-flex tw-text-[20px] cart-button"
                      :data-test="`remove-alternative-domain-${i}`"
                    />
                    <IconButton
                      v-else
                      @click="selectPackage(pkg)"
                      :icon="{
                        default: { icon: 'fa-regular fa-cart-plus' },
                        hover: { icon: 'fa-solid fa-cart-plus' },
                      }"
                      class="tw-flex tw-text-[20px] cart-button"
                      :data-test="`add-alternative-domain-${i}`"
                    />
                  </div>
                </div>
                <div class="my-4 d-flex justify-content-around">
                  <div>
                    <div class="mb-2">
                      <Btn
                        @click="goToHosting"
                        :loading="searchStatus.isLoading"
                        :disabled="
                          searchStatus.isLoading || !cartItems.length || !searchValue.length
                        "
                      >
                        Proceed to Hosting Options
                      </Btn>
                    </div>
                    <div v-if="cartItems.length">
                      <Btn
                        variant="link"
                        @click="goToCart"
                        :loading="searchStatus.isLoading"
                        :disabled="
                          searchStatus.isLoading || !cartItems.length || !searchValue.length
                        "
                      >
                        No thanks, just the domains
                      </Btn>
                    </div>
                  </div>
                </div>
              </template>
            </div>
          </template>
        </div>
        <template v-if="showCustomContent">
          <div class="content">
            <div class="section">
              <a name="about-nexcess" />
              <h3>About Nexcess</h3>
              <p>
                Nexcess is a top-rated hosting provider that specializes in helping businesses
                succeed. Our customers enjoy a fully managed experience, so they can focus on
                running their company. Grow and scale your business with built-in performance tools,
                lightning-fast load times and enterprise-grade security. With 24 years of hosting
                experience and 500,000+ websites under management, Nexcess helps you worry less and
                do more.
              </p>
              <div class="mt-5">
                <a href="#top">Back to Top</a>
              </div>
            </div>
          </div>
          <div class="content">
            <div class="section">
              <a name="what-is-managed-hosting" />
              <h3>What is Managed Hosting?</h3>
              <p>
                Hosting is what makes your website available on the internet. Traditional hosting
                (like GoDaddy) is cheap for a reason. It’s because they don’t keep your site
                updated, maintained and optimized. So if your site crashes, it’s up to you to find
                the cause (even if it’s 3AM). If you’re not a tech person, it could be days before
                you’re back online.
              </p>
              <p>
                Nexcess makes sure that never happens. With 99.998% uptime, we handle maintenance,
                software updates, speed and security so you can rest easy. And if you ever have a
                hosting question or a crisis, our helpful experts are here for you 24/7/365 to
                resolve the issue fast.
              </p>
              <div class="mt-5">
                <a href="#top">Back to Top</a>
              </div>
            </div>
          </div>
          <div class="content">
            <div class="section">
              <a name="compare-hosting-providers" />
              <h3>Compare Hosting Providers</h3>
              <p>
                Nexcess gives you more premium features than any other hosting provider. You’re
                gonna love it. And if you don’t, we offer a 30-day money-back guarantee.
              </p>
            </div>
          </div>
          <div class="content">
            <div class="section">
              <BrandComparisonTable />
              <div class="mt-5">
                <a href="#top">Back to Top</a>
              </div>
            </div>
          </div>
        </template>
      </div>
      <ChatEyeCatcher class="desktop" department="Sales" />
    </div>
  </GlobalNav>
</template>

<script>
import { computed, onMounted, ref } from "vue";
import BrandComparisonTable from "@/components/display/BrandComparisonTable";
import Btn from "@/components/display/Btn";
import { CartDomainItem } from "@/utils/cart";
import ChatEyeCatcher from "@/components/display/ChatEyeCatcher";
import constants from "@/utils/constants";
import DropDownButton from "@/components/display/DropDownButton";
import { formatters } from "leatherman-js";
import FormInput from "@/components/display/form/FormInput";
import GlobalNav from "@/components/nav/GlobalNav";
import IconButton from "@/components/display/IconButton.vue";
import LoadingState from "@/components/display/LoadingState";
import router from "@/router";
import store from "@/store";
import Tooltip from "@/components/display/Tooltip";

export default {
  name: "DomainSearch",
  components: {
    BrandComparisonTable,
    Btn,
    ChatEyeCatcher,
    DropDownButton,
    FormInput,
    GlobalNav,
    IconButton,
    LoadingState,
    Tooltip,
  },
  props: {
    search: String,
    irpid: String,
  },
  setup(props) {
    const cartItems = computed(
      () => store.state.cart.items.filter(({ orderType }) => orderType === "domain") || []
    );
    const searchValue = ref(props.search || "");
    const domain = ref("");
    const tld = ref("");
    const searchStatus = computed(() => store.state.domainPackages.searchStatus);
    const status = computed(() => store.state.domainPackages.status);

    const getItemByPackage = (pkg) =>
      cartItems.value.find(
        (item) => item.domainName === domain.value && pkg.tld.id === item.tld.id
      );
    // all listed packages
    const alternativePackages = computed(() =>
      store.state.domainPackages.list
        .filter((pkg) => pkg.tld?.identity !== tld.value)
        .map((pkg) => {
          pkg.inCart = !!getItemByPackage(pkg);
          return pkg;
        })
    );
    // currently selected package
    const selectedPackage = computed(() => {
      let pkg = store.state.domainPackages.list.find((pkg) => pkg.tld.identity === tld.value);
      if (!pkg) {
        pkg = store.state.domainPackages.list[0];
      }
      pkg.inCart = !!getItemByPackage(pkg);
      return pkg;
    });
    // totle price of all selected packages
    const total = computed(
      () => cartItems.value.reduce((acc, item) => acc + Number(item.package.pricing["12"]), 0) || 0
    );
    // remove item from the cart
    const removeItem = (item) => store.dispatch("cart/removeItem", item);
    // deselect package in the ui (and remove item attached to it from the cart)
    const removePackage = (pkg) => removeItem(getItemByPackage(pkg));
    // add a package to the cart
    const selectPackage = (pkg) => {
      const item = new CartDomainItem({
        packageId: pkg.id,
        package: pkg,
        term: 12,
        domainName: domain.value,
        tld: pkg.tld,
      });
      store.dispatch("cart/addItem", item);
    };

    const isAvailable = ref(false);
    const searchDomains = async () => {
      const valid = searchValue.value.indexOf(".") !== -1;

      // get domain and tld from search value
      domain.value = searchValue.value.substring(0, searchValue.value.indexOf("."));
      tld.value = searchValue.value.substring(searchValue.value.indexOf(".") + 1);

      if (!valid) {
        await store.dispatch("domainPackages/search", searchValue.value);

        domain.value = selectedPackage.value.domain.split(".")[0];
        tld.value = selectedPackage.value.domain.split(".")[1];
        isAvailable.value = !!alternativePackages.value.length;
      } else {
        const response = await store.dispatch("domainPackages/search", searchValue.value);
        isAvailable.value = response?.domain?.is_available && response?.domain?.package?.tld;
      }
    };
    // go to hosting options before checking out
    const goToHosting = () => {
      router.push({ name: "checkout-package" });
    };
    const goToCart = () => {
      const planItem = store.state.cart.items.find((item) => item.orderType === "virt-guest-cloud");
      if (planItem) {
        store.dispatch("cart/removeItem", planItem);
        store.dispatch("packages/site/clearItem");
      }
      router.push({ name: "checkout-domains" });
    };

    // partner logos to show to customers referred by a partner
    const partnerLogos = {
      "business-rocket": require("@/assets/business-rocket-logo.svg"),
    };
    // partner company names to show to customers referred by a partner
    const partnerNames = {
      "business-rocket": "BusinessRocketⓇ",
    };
    const partnerLogo = computed(() => partnerLogos[constants.IRPIDS[props.irpid]]);
    const partnerName = computed(() => partnerNames[constants.IRPIDS[props.irpid]]);
    const showCustomContent = computed(() => constants.IRPIDS[props.irpid]);

    onMounted(async () => {
      store.dispatch("domainPackages/reset");
      if (props.search) {
        searchDomains();
      }
    });

    return {
      tld,
      searchValue,
      alternativePackages,
      removePackage,
      selectPackage,
      removeItem,
      moneyFormat: formatters.money.moneyFormat,
      status,
      domain,
      selectedPackage,
      searchStatus,
      goToHosting,
      goToCart,
      cartItems,
      total,
      partnerLogo,
      partnerName,
      isAvailable,
      showCustomContent,
      searchDomains,
    };
  },
};
</script>

<style lang="scss" scoped>
@import "~@/styles/variables.scss";

.content {
  @apply tw-bg-background tw-border tw-border-border;
  padding: 0rem;
  width: 800px;
  margin-bottom: 1.5rem;

  .section {
    padding: 1.5rem;
  }
}

.cart-preview {
  min-width: 18rem;
}

.partner-logo {
  width: 300px;
}

.partner-message {
  width: 400px;
}

.cart-item-row {
  border-width: 1px;
}

.cart-button {
  margin-left: 1.5rem !important;
}

.sad-icon,
.happy-icon {
  width: 2rem;
  height: 2rem;
  margin-right: 1rem;
}

.domain-message {
  font-size: 1.125rem;
}
</style>
